import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, Plus, ChevronRight } from 'lucide-react'
import api from '../../services/api'

const RED    = '#FF3B5C'
const ORANGE = '#FF9500'
const GREEN  = '#00D4AA'
const BLUE   = '#0A84FF'
const NAVY   = '#1B2B4B'

const SEVERITY_COLOR: Record<string, string> = {
  critical: RED,
  high: ORANGE,
  medium: '#FFD60A',
  low: GREEN,
}
const STATUS_COLOR: Record<string, string> = {
  open: RED,
  in_progress: ORANGE,
  closed: GREEN,
  under_review: BLUE,
}

const FILTERS = [
  ['open', 'Open'],
  ['in_progress', 'In Progress'],
  ['closed', 'Closed'],
  ['all', 'All'],
]

export default function MobileIncidents() {
  const navigate = useNavigate()
  const [incidents, setIncidents] = useState<any[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('open')

  const load = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (filter !== 'all') params.set('status', filter)
      const { data } = await api.get(`/incidents?${params}&limit=50`)
      setIncidents(data.data || [])
      setTotal(data.total || 0)
    } catch {}
    setLoading(false)
  }

  useEffect(() => { load() }, [filter])

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100%', paddingBottom: 96 }}>
      {/* Sticky header */}
      <div
        className="sticky top-0 z-10"
        style={{ background: 'var(--bg)', borderBottom: '1px solid rgba(255,255,255,0.06)', padding: '16px 20px 12px' }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12 }}>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: '#FFFFFF', margin: 0 }}>Incidents</h1>
          <span style={{ fontSize: 12, fontWeight: 500, color: 'rgba(255,255,255,0.3)' }}>{total} total</span>
        </div>
        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', scrollbarWidth: 'none' }}>
          {FILTERS.map(([val, label]) => {
            const active = filter === val
            return (
              <button
                key={val}
                onClick={() => setFilter(val)}
                style={{
                  padding: '6px 14px',
                  borderRadius: 999,
                  fontSize: 12,
                  fontWeight: 600,
                  flexShrink: 0,
                  border: 'none',
                  cursor: 'pointer',
                  background: active ? RED : 'rgba(255,255,255,0.08)',
                  color: active ? '#FFFFFF' : 'rgba(255,255,255,0.45)',
                  transition: 'all 150ms',
                }}
              >
                {label}
              </button>
            )
          })}
        </div>
      </div>

      {/* List */}
      <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '64px 0', color: 'rgba(255,255,255,0.3)' }}>Loading…</div>
        ) : incidents.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '80px 0', gap: 12 }}>
            <div style={{ width: 60, height: 60, borderRadius: '50%', background: `${RED}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <AlertTriangle size={26} color={RED} />
            </div>
            <p style={{ fontWeight: 600, fontSize: 15, color: '#FFFFFF', margin: 0 }}>No incidents</p>
            <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.35)', margin: 0 }}>
              {filter === 'all' ? 'Nothing recorded yet' : `No ${filter.replace('_', ' ')} incidents`}
            </p>
            <button
              onClick={() => navigate('/m/incidents/new')}
              style={{ marginTop: 4, padding: '10px 20px', borderRadius: 999, fontSize: 13, fontWeight: 600, color: '#FFFFFF', background: RED, border: 'none', cursor: 'pointer' }}
            >
              Report Incident
            </button>
          </div>
        ) : (
          incidents.map(inc => {
            const severityColor = SEVERITY_COLOR[inc.ai_severity] || 'rgba(255,255,255,0.2)'
            const statusColor = STATUS_COLOR[inc.status] || 'rgba(255,255,255,0.4)'
            return (
              <button
                key={inc.id}
                onClick={() => navigate(`/m/incidents/${inc.id}`)}
                style={{
                  background: 'var(--white)',
                  border: 'none',
                  borderRadius: 18,
                  borderLeft: `3px solid ${severityColor}`,
                  textAlign: 'left',
                  cursor: 'pointer',
                  overflow: 'hidden',
                  width: '100%',
                }}
                className="active:scale-[0.98] transition-transform"
              >
                <div style={{ padding: '14px 14px 14px 16px' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 10, fontFamily: 'monospace', fontWeight: 600, color: 'rgba(255,255,255,0.4)' }}>
                          {inc.incident_number}
                        </span>
                        <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 999, fontWeight: 600, textTransform: 'capitalize', background: `${statusColor}20`, color: statusColor }}>
                          {inc.status?.replace(/_/g, ' ')}
                        </span>
                        {inc.ai_severity && (
                          <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 999, fontWeight: 600, textTransform: 'capitalize', background: `${severityColor}20`, color: severityColor }}>
                            {inc.ai_severity}
                          </span>
                        )}
                      </div>
                      <p style={{ fontSize: 13, fontWeight: 500, color: '#FFFFFF', margin: 0, lineHeight: 1.45, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {inc.description || inc.title}
                      </p>
                      <div style={{ display: 'flex', gap: 12, marginTop: 7 }}>
                        {inc.site_name && <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>{inc.site_name}</span>}
                        <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>
                          {new Date(inc.date_occurred).toLocaleDateString('en-AU')}
                        </span>
                      </div>
                    </div>
                    <ChevronRight size={15} color="rgba(255,255,255,0.18)" style={{ flexShrink: 0, marginTop: 2 }} />
                  </div>
                </div>
              </button>
            )
          })
        )}
      </div>

      {/* FAB */}
      <button
        onClick={() => navigate('/m/incidents/new')}
        style={{
          position: 'fixed',
          bottom: 'calc(64px + env(safe-area-inset-bottom) + 16px)',
          right: 20,
          width: 52,
          height: 52,
          borderRadius: '50%',
          background: RED,
          boxShadow: `0 0 24px ${RED}55`,
          border: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
        }}
        className="active:scale-95 transition-transform"
      >
        <Plus size={24} color="white" />
      </button>
    </div>
  )
}
